import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface WeightGameProps {
  weightHistory: Array<{ weight: number; date: string }>
  onClose: () => void
}

interface Bird {
  y: number
  velocity: number
}

interface Pipe {
  x: number
  height: number
  passed: boolean
}

// Adjusted constants for much slower gameplay
const GRAVITY = 0.10         // Reduced from 0.3
const JUMP_FORCE = -4         // Reduced from -6
const PIPE_SPEED = 1          // Reduced from 2
const PIPE_WIDTH = 50
const PIPE_GAP = 180          // Increased from 170
const BIRD_SIZE = 30
const PIPE_SPAWN_RATE = 240   // Increased from 180

export default function WeightGame({ weightHistory, onClose }: WeightGameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [gameOver, setGameOver] = useState(false)
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const gameStateRef = useRef({
    bird: { y: 250, velocity: 0 },
    pipes: [] as Pipe[],
    frameCount: 0,
    animationFrameId: 0,
  })

  const startGame = () => {
    if (!isPlaying) {
      gameStateRef.current = {
        bird: { y: 250, velocity: 0 },
        pipes: [],
        frameCount: 0,
        animationFrameId: 0,
      }
      setScore(0)
      setGameOver(false)
      setIsPlaying(true)
    }
  }

  const handleJump = () => {
    if (!gameOver) {
      gameStateRef.current.bird.velocity = JUMP_FORCE
    } else {
      startGame()
    }
  }

  // Handle both click and spacebar
  const handleClick = () => {
    handleJump()
  }

  useEffect(() => {
    // Add keyboard listener for spacebar
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault() // Prevent page scrolling
        handleJump()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [gameOver, isPlaying])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const gameLoop = () => {
      if (!isPlaying) return
      const state = gameStateRef.current

      // Update bird
      state.bird.velocity += GRAVITY
      state.bird.y += state.bird.velocity

      // Generate new pipes
      if (state.frameCount % PIPE_SPAWN_RATE === 0) {
        const height = Math.random() * (canvas.height - PIPE_GAP - 100) + 50
        state.pipes.push({
          x: canvas.width,
          height,
          passed: false,
        })
      }

      // Update pipes
      state.pipes.forEach((pipe) => {
        pipe.x -= PIPE_SPEED

        // Check for score
        if (!pipe.passed && pipe.x + PIPE_WIDTH < canvas.width / 3) {
          pipe.passed = true
          setScore((prev) => {
            const newScore = prev + 1
            setHighScore((h) => Math.max(h, newScore))
            return newScore
          })
        }

        // Check for collisions
        const birdX = canvas.width / 3
        if (
          birdX + BIRD_SIZE > pipe.x &&
          birdX < pipe.x + PIPE_WIDTH &&
          (state.bird.y < pipe.height || state.bird.y + BIRD_SIZE > pipe.height + PIPE_GAP)
        ) {
          setGameOver(true)
          setIsPlaying(false)
        }
      })

      // Remove off-screen pipes
      state.pipes = state.pipes.filter((pipe) => pipe.x + PIPE_WIDTH > 0)

      // Check boundaries
      if (state.bird.y < 0 || state.bird.y + BIRD_SIZE > canvas.height) {
        setGameOver(true)
        setIsPlaying(false)
      }

      // Draw everything
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw background
      ctx.fillStyle = '#0f172a'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw pipes
      ctx.fillStyle = '#22c55e'
      state.pipes.forEach((pipe) => {
        // Top pipe
        ctx.fillRect(pipe.x, 0, PIPE_WIDTH, pipe.height)
        // Bottom pipe
        ctx.fillRect(
          pipe.x,
          pipe.height + PIPE_GAP,
          PIPE_WIDTH,
          canvas.height - (pipe.height + PIPE_GAP)
        )
      })

      // Draw bird
      ctx.fillStyle = '#60a5fa'
      ctx.beginPath()
      ctx.arc(
        canvas.width / 3,
        state.bird.y + BIRD_SIZE / 2,
        BIRD_SIZE / 2,
        0,
        Math.PI * 2
      )
      ctx.fill()

      state.frameCount++
      state.animationFrameId = requestAnimationFrame(gameLoop)
    }

    startGame()
    gameLoop()

    return () => {
      if (gameStateRef.current.animationFrameId) {
        cancelAnimationFrame(gameStateRef.current.animationFrameId)
      }
    }
  }, [isPlaying])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative rounded-xl bg-slate-800/90 p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-slate-400 hover:bg-slate-700/50 hover:text-white"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <h2 className="mb-4 text-xl font-semibold text-white">Weight Flappy Game</h2>
        
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={600}
            height={400}
            className="rounded-lg border border-slate-600"
            onClick={handleClick}
          />
          
          <div className="absolute left-4 top-4 rounded-lg bg-slate-800/80 px-3 py-1 text-white">
            Score: {score}
          </div>
          
          <div className="absolute right-4 top-4 rounded-lg bg-slate-800/80 px-3 py-1 text-white">
            High Score: {highScore}
          </div>

          {gameOver && (
            <div className="absolute inset-0 flex flex-col items-center justify-center rounded-lg bg-black/60">
              <p className="mb-2 text-2xl font-bold text-white">Game Over!</p>
              <p className="text-lg text-slate-300">Click to play again</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
} 